import { useTranslation } from 'react-i18next'

import { useGrantsUtils } from '../../../utils/utils'

import { CardContent, CardDashboard, Flex, MarkdownPreview, Copy, Badge } from '@percent/lemonade'
import { ProgramOverviewProps } from './ProgramOverview.types'
import {
  GrantProgramCandidateRule,
  GrantProgramEntryType
} from '@percent/admin-dashboard/api/actions/grantProgram/grantProgram.types'

export function ProgramOverview({ program }: Readonly<ProgramOverviewProps>) {
  const { t } = useTranslation()
  const { formatDate, formatCurrency } = useGrantsUtils()

  return (
    <Flex direction="column" gap={16} data-testid="program-overview">
      <CardDashboard
        title={t('grants.programDetails.overview.description.title')}
        data-testid="program-description-card"
      >
        <CardContent testId="program-description">
          <MarkdownPreview
            content={program?.description ?? t('grants.programDetails.overview.noDescription')}
            fontSize="bodySmall"
          />
        </CardContent>
      </CardDashboard>
      <Flex direction="column" bg="white">
        <CardDashboard title={t('grants.programDetails.overview.details.title')} data-testid="program-details-card">
          <CardContent title={t('grants.programDetails.overview.name')} testId="program-name">
            {program.name}
          </CardContent>
          <CardContent title={t('grants.programDetails.overview.createdAt')} testId="created-at">
            {formatDate(program.createdAt)}
          </CardContent>
          <CardContent title={t('grants.programDetails.overview.opensAt')} testId="opens-at">
            {formatDate(program.opensAt)}
          </CardContent>
          <CardContent title={t('grants.programDetails.overview.closesAt')} testId="closes-at">
            {formatDate(program.closesAt)}
          </CardContent>
          <CardContent title={t('grants.programDetails.overview.budgetMin')} testId="budget-min">
            {formatCurrency(program.budgetMin, program.currency)}
          </CardContent>
          <CardContent title={t('grants.programDetails.overview.budgetMax')} testId="budget-max">
            {formatCurrency(program.budgetMax, program.currency)}
          </CardContent>
          <CardContent title={t('grants.programDetails.overview.currency')} testId="currency">
            {program.currency}
          </CardContent>
          {program.validationSubmissionConfigurationId && (
            <CardContent
              title={t('grants.programDetails.overview.validationSubmissionConfigurationId')}
              testId="validation-submission-configuration-id"
            >
              <Copy text={program.validationSubmissionConfigurationId}>
                {program.validationSubmissionConfigurationId}
              </Copy>
            </CardContent>
          )}
          <CardContent title={t('grants.programDetails.overview.nonprofitCcEmail')} testId="nonprofit-cc-email">
            {program.nonprofitCcEmail ?? t('status.na')}
          </CardContent>
          <CardContent
            title={t('grants.programDetails.overview.emailSuppressionConfig')}
            testId="email-suppression-config"
          >
            {program.emailSuppressionConfig?.length ? (
              <Flex gap={4} wrap="wrap">
                {program.emailSuppressionConfig.map(name => (
                  <Badge key={name} variant="default">
                    {name}
                  </Badge>
                ))}
              </Flex>
            ) : (
              t('status.na')
            )}
          </CardContent>
          <CardContent title={t('grants.programDetails.overview.candidateRule')} testId="candidate-rule">
            <Badge variant="default">
              {program.candidateRule === GrantProgramCandidateRule.UNIQUE_PER_ORG
                ? t('grants.programDetails.overview.candidateRule.uniquePerOrg')
                : t('grants.programDetails.overview.candidateRule.uniquePerNomination')}
            </Badge>
          </CardContent>
        </CardDashboard>
        <CardDashboard
          title={t('grants.programDetails.overview.submissionDetails.title')}
          data-testid="program-submission-details-card"
        >
          <CardContent title={t('grants.programDetails.overview.entryType')} testId="entry-type">
            <Badge variant="default">
              {
                {
                  [GrantProgramEntryType.NOMINATION]: t('grants.programDetails.overview.entryType.nomination'),
                  [GrantProgramEntryType.APPLICATION]: t('grants.programDetails.overview.entryType.application'),
                  [GrantProgramEntryType.INVITATION]: t('grants.programDetails.overview.entryType.invitation')
                }[program.entryType]
              }
            </Badge>
          </CardContent>
          {program.nominationFormId && (
            <CardContent title={t('grants.programDetails.overview.entryFormId')} testId="entry-form-id">
              <Copy text={program.nominationFormId || ''}>{program.nominationFormId}</Copy>
            </CardContent>
          )}
          <CardContent title={t('grants.programDetails.overview.entryFormDeadline')} testId="entry-form-deadline">
            {formatDate(program.nominationDeadline)}
          </CardContent>
          {program.entryType === GrantProgramEntryType.NOMINATION && (
            <CardContent
              title={t('grants.programDetails.overview.shareNominatorInfoWithOrg')}
              testId="share-info-with-org"
            >
              {program.shareNominatorInfoWithOrg ? t('status.yes') : t('status.no')}
            </CardContent>
          )}
          <CardContent
            title={t('grants.programDetails.overview.entrySubmissionsEnabled')}
            testId="entry-submissions-enabled"
          >
            <Badge variant={program.nominationsEnabled ? 'positive' : 'critical'}>
              {program.nominationsEnabled
                ? t('grants.programDetails.overview.entrySubmissionsEnabled.Yes')
                : t('grants.programDetails.overview.entrySubmissionsEnabled.No')}
            </Badge>
          </CardContent>
        </CardDashboard>
      </Flex>
    </Flex>
  )
}
